import { c as _c } from "react/compiler-runtime";
import { useMemo } from 'react';
import type { DimensionsMap, DimensionsValuesMap, MetricsMap, MetricsVariantsMap, ValidatedDataDefinitionProps } from '../internalTypes';
import type { ChartCardClientDataProps, ChartCardDataDefinitionProps, ChartDataRow } from '../types';
import { createDimensionsMap, createMetricsMap, createMetricsVariantsMap } from '../utils/choices';
import { errorLocal } from '../utils/generics';
import { getDimensionValues } from '../utils/data';
const EMPTY_BOOTSTRAP_DATA: never[] = [];
const useValidateChartDataAndDefinitionCommonProps = (t0, dataProps, options) => {
  const $ = _c(30);
  const {
    dimensions,
    metrics
  } = t0;
  const dataIsLoading = dataProps?.isLoading === true;
  const data = dataProps?.data ?? (options?.allowEmptyBootstrap === true ? EMPTY_BOOTSTRAP_DATA as DataRow[] : undefined);
  const chartLoading = dataIsLoading || data == null;
  const enableNullDimensionValue = options?.enableNullDimensionValue ?? true;
  let t1;
  bb0: {
    if (chartLoading) {
      t1 = undefined;
      break bb0;
    }
    let t2;
    if ($[0] !== data || $[1] !== dimensions) {
      t2 = createDimensionsMap(dimensions, data);
      $[0] = data;
      $[1] = dimensions;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    const dimensionsMap = t2;
    if (dimensionsMap == null) {
      errorLocal("useValidateChartDataAndDefinitionCommonProps dimensionsMap null", {
        dimensions,
        data
      });
    }
    t1 = dimensionsMap;
  }
  const dimensionsMap_0 = t1;
  let t2;
  if ($[3] !== dimensions) {
    t2 = createMetricsVariantsMap(dimensions);
    $[3] = dimensions;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const metricsVariantMap = t2;
  if (metricsVariantMap == null) {
    errorLocal("useValidateChartDataAndDefinitionCommonProps metricsVariantMap null", {
      dimensions
    });
  }
  const metricsVariantMap_0 = metricsVariantMap;
  let t3;
  bb1: {
    if (chartLoading) {
      t3 = undefined;
      break bb1;
    }
    if (dimensionsMap_0 == null) {
      errorLocal("useValidateChartDataAndDefinitionCommonProps metricsMap dimensionsMap null");
      t3 = undefined;
      break bb1;
    }
    let t4;
    if ($[5] !== dimensionsMap_0 || $[6] !== metrics) {
      t4 = createMetricsMap(metrics, dimensionsMap_0);
      $[5] = dimensionsMap_0;
      $[6] = metrics;
      $[7] = t4;
    } else {
      t4 = $[7];
    }
    const metricsMap = t4;
    if (metricsMap == null) {
      errorLocal("useValidateChartDataAndDefinitionCommonProps metricsMap null", {
        metrics,
        dimensionsMap: dimensionsMap_0
      });
    }
    t3 = metricsMap;
  }
  const metricsMap_0 = t3;
  let t4;
  bb2: {
    if (chartLoading) {
      t4 = undefined;
      break bb2;
    }
    if (dimensionsMap_0 == null) {
      errorLocal("useValidateChartDataAndDefinitionCommonProps dimensionsValuesMap dimensionsMap null");
      t4 = undefined;
      break bb2;
    }
    let t5;
    if ($[8] !== data || $[9] !== dimensions || $[10] !== enableNullDimensionValue) {
      let t6;
      if ($[12] !== data || $[13] !== enableNullDimensionValue) {
        t6 = (acc, dimension) => {
          if (acc == null) {
            return;
          }
          const {
            id
          } = dimension;
          if (id in acc) {
            errorLocal(`useValidateChartDataAndDefinitionCommonProps dimensionsValuesMap ${id} already in acc`, acc);
            return;
          }
          acc[id] = getDimensionValues(data, dimension, enableNullDimensionValue);
          return acc;
        };
        $[12] = data;
        $[13] = enableNullDimensionValue;
        $[14] = t6;
      } else {
        t6 = $[14];
      }
      t5 = dimensions.reduce(t6, {});
      $[8] = data;
      $[9] = dimensions;
      $[10] = enableNullDimensionValue;
      $[11] = t5;
    } else {
      t5 = $[11];
    }
    const dimensionsValuesMap = t5 as DimensionsValuesMap<DataKey> | undefined;
    if (dimensionsValuesMap == null) {
      errorLocal("useValidateChartDataAndDefinitionCommonProps dimensionsValuesMap null", {
        dimensions,
        dimensionsMap: dimensionsMap_0,
        data
      });
    }
    t4 = dimensionsValuesMap;
  }
  const dimensionsValuesMap_0 = t4;
  if (dimensionsMap_0 != null && metricsVariantMap_0 != null && metricsMap_0 != null && dimensionsValuesMap_0 != null) {
    let t5;
    if ($[15] !== dimensionsMap_0 || $[16] !== dimensionsValuesMap_0 || $[17] !== metricsMap_0 || $[18] !== metricsVariantMap_0) {
      t5 = {
        result: "valid",
        dimensionsMap: dimensionsMap_0,
        metricsVariantMap: metricsVariantMap_0,
        metricsMap: metricsMap_0,
        dimensionsValuesMap: dimensionsValuesMap_0
      };
      $[15] = dimensionsMap_0;
      $[16] = dimensionsValuesMap_0;
      $[17] = metricsMap_0;
      $[18] = metricsVariantMap_0;
      $[19] = t5;
    } else {
      t5 = $[19];
    }
    return t5;
  }
  if (chartLoading) {
    let t5;
    if ($[20] !== dimensionsMap_0 || $[21] !== dimensionsValuesMap_0 || $[22] !== metricsMap_0 || $[23] !== metricsVariantMap_0) {
      t5 = {
        result: "loading",
        dimensionsMap: dimensionsMap_0,
        metricsVariantMap: metricsVariantMap_0,
        metricsMap: metricsMap_0,
        dimensionsValuesMap: dimensionsValuesMap_0
      };
      $[20] = dimensionsMap_0;
      $[21] = dimensionsValuesMap_0;
      $[22] = metricsMap_0;
      $[23] = metricsVariantMap_0;
      $[24] = t5;
    } else {
      t5 = $[24];
    }
    return t5;
  }
  errorLocal("useValidateChartDataAndDefinitionCommonProps invalid", {
    dimensionsMap: dimensionsMap_0,
    metricsVariantMap: metricsVariantMap_0,
    metricsMap: metricsMap_0,
    dimensionsValuesMap: dimensionsValuesMap_0,
    dimensions,
    metrics
  });
  let t5;
  if ($[25] !== dimensionsMap_0 || $[26] !== dimensionsValuesMap_0 || $[27] !== metricsMap_0 || $[28] !== metricsVariantMap_0) {
    t5 = {
      result: "invalid",
      dimensionsMap: dimensionsMap_0,
      metricsVariantMap: metricsVariantMap_0,
      metricsMap: metricsMap_0,
      dimensionsValuesMap: dimensionsValuesMap_0
    };
    $[25] = dimensionsMap_0;
    $[26] = dimensionsValuesMap_0;
    $[27] = metricsMap_0;
    $[28] = metricsVariantMap_0;
    $[29] = t5;
  } else {
    t5 = $[29];
  }
  return t5;
};
export default useValidateChartDataAndDefinitionCommonProps;